import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Form from "react-validation/build/form";
import Input from "react-validation/build/input";
import CheckButton from "react-validation/build/button";
import { clearMessage } from "../../../reducers/message";
import {
    completeLoginChallenge,
    isAccountDeletionRecoveryRequired,
    login,
    resendLoginChallenge,
    startSso,
} from "../../../services/auth";
import { ACCOUNT_DELETION_ROUTES } from "../../../constants/accountDeletionRoutes";
import { isValidEmail } from "../../../constants/authValidation";
import {
    clearPendingLoginChallenge,
    persistPendingLoginChallenge,
    readPendingLoginChallenge,
} from "../../../services/authLoginChallengeStorage";
import {
    sanitizePostLoginRedirect,
} from "../../../services/authPostLoginRedirectStorage";
import LiquidGlassCard from "../LiquidGlassCard";
import PopupMessage from "../PopupMessage";
import ContinueWithGoogleButton from "../../atoms/ContinueWithGoogleButton";
import AuthBrandTitle from "../AuthBrandTitle";
import "../AuthSupportCard.css";
import "./index.css";

const INITIAL_FORM_DATA = {
    email: "",
    password: "",
};

const VERIFICATION_CODE_LENGTH = 6;

/* Inline field error. Two things the previous markup got wrong: it reused the
   global `.alert.alert-danger` slab, which had the same radius, padding and
   width as the input and so read as a second field rather than a note about the
   first; and it was a bare `<div>`, which assistive tech passed over in silence.
   This is a quiet line of text under its field, and it announces itself. */
const renderFieldError = (message) => (
    <p className="auth-field-error" role="alert">
        <svg
            className="auth-field-error__icon"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <circle cx="8" cy="8" r="6.9" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 4.6v4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="8" cy="11.4" r="0.95" fill="currentColor" />
        </svg>
        {message}
    </p>
);

const parseServerTimestamp = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatDuration = (seconds) => {
    const safeSeconds = Math.max(0, seconds);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const LoginCard = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [loginChallenge, setLoginChallenge] = useState(() => readPendingLoginChallenge());
    const [verificationCode, setVerificationCode] = useState("");
    const [loadingAction, setLoadingAction] = useState(null);
    const [message, setMessage] = useState("");
    const [popupTrigger, setPopupTrigger] = useState(0);
    const [ssoLoading, setSsoLoading] = useState(null);
    const [accountDeletionRecoveryPrompt, setAccountDeletionRecoveryPrompt] = useState(null);
    const [now, setNow] = useState(Date.now());

    const { message: reduxMessage } = useSelector((state) => state.message);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const postLoginRedirect = useMemo(
        () => sanitizePostLoginRedirect(location.state?.from),
        [location.state]
    );

    const form = useRef();
    const checkBtn = useRef();
    const verificationInputRef = useRef(null);

    const requiredValidator = useMemo(() => (value) => {
        if (!value || value.trim() === "") {
            return renderFieldError(t("auth.common.validation.required"));
        }
        return null;
    }, [t]);

    /* Format check the browser used to do, now rendered inline in the app's own
       styling and language instead of a native bubble. Runs only once there is
       something to check, so `requiredValidator` still owns the empty case. */
    const emailFormatValidator = useMemo(() => (value) => {
        if (!value || value.trim() === "" || isValidEmail(value)) {
            return null;
        }
        return renderFieldError(t("auth.common.validation.invalidEmail"));
    }, [t]);

    useEffect(() => {
        if (reduxMessage) {
            setMessage(reduxMessage);
        }
    }, [reduxMessage]);

    useEffect(() => {
        const flashMessageKey = location.state?.authFlashMessageKey;
        if (typeof flashMessageKey !== "string" || flashMessageKey.trim() === "") {
            return;
        }

        setMessage(t(flashMessageKey));
        setPopupTrigger((prev) => prev + 1);
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
            },
            {
                replace: true,
                state: null,
            }
        );
    }, [location.pathname, location.search, location.state, navigate, t]);

    useEffect(() => {
        dispatch(clearMessage());

        return () => {
            dispatch(clearMessage());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!loginChallenge) {
            return undefined;
        }

        setNow(Date.now());
        const intervalId = window.setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [loginChallenge]);

    useEffect(() => {
        if (!loginChallenge) {
            return;
        }

        verificationInputRef.current?.focus();
    }, [loginChallenge]);

    const verificationWindowRemaining = loginChallenge
        ? Math.max(0, Math.ceil((loginChallenge.expiresAt - now) / 1000))
        : 0;
    const resendCooldownRemaining = loginChallenge
        ? Math.max(0, Math.ceil((loginChallenge.resendAvailableAt - now) / 1000))
        : 0;
    const isChallengeExpired = Boolean(loginChallenge) && verificationWindowRemaining === 0;
    const isLoggingIn = loadingAction === "login";
    const isCompletingChallenge = loadingAction === "challenge";
    const isResendingChallenge = loadingAction === "resend";

    const showPopup = (nextMessage) => {
        setMessage(nextMessage);
        setPopupTrigger((prev) => prev + 1);
    };

    const clearLoginFeedback = useCallback(() => {
        setMessage("");
        dispatch(clearMessage());
    }, [dispatch]);

    const getChallengeValidationMessage = () => {
        if (!verificationCode) {
            return t("auth.common.validation.required");
        }
        if (!new RegExp(`^\\d{${VERIFICATION_CODE_LENGTH}}$`).test(verificationCode)) {
            return t("auth.login.challenge.validation.codeLength");
        }
        return null;
    };

    const getChallengeErrorMessage = (error) => {
        const status = error?.response?.status;

        if (status === 400) {
            return t("auth.login.challenge.feedback.invalidCode");
        }

        if (status === 404 || status === 409) {
            return t("auth.login.challenge.feedback.challengeExpired");
        }

        return t("auth.login.challenge.feedback.genericError");
    };

    const updateChallengeState = (challengeResponse) => {
        const issuedAt = parseServerTimestamp(challengeResponse?.issuedAt);
        const expiresAt = parseServerTimestamp(challengeResponse?.expiresAt);
        const resendAvailableAt = parseServerTimestamp(challengeResponse?.resendAvailableAt);
        const nextChallenge = (Number.isFinite(issuedAt) || Number.isFinite(expiresAt))
            ? persistPendingLoginChallenge(challengeResponse?.challengeId, {
                ...(Number.isFinite(issuedAt) ? { issuedAt } : {}),
                ...(Number.isFinite(expiresAt) ? { expiresAt } : {}),
                ...(Number.isFinite(resendAvailableAt) ? { resendAvailableAt } : {}),
            })
            : persistPendingLoginChallenge(challengeResponse?.challengeId);
        setLoginChallenge(nextChallenge);
        setVerificationCode("");
        setFormData((current) => ({
            ...current,
            password: "",
        }));
        setNow(Date.now());
    };

    const resetChallengeStep = () => {
        clearPendingLoginChallenge();
        setLoginChallenge(null);
        setVerificationCode("");
        setNow(Date.now());
    };

    const onChange = (event) => {
        const value = event.target.value;
        const name = event.target.name;
        setAccountDeletionRecoveryPrompt(null);
        setFormData((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleVerificationCodeChange = (event) => {
        const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, VERIFICATION_CODE_LENGTH);
        setVerificationCode(digitsOnly);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoadingAction("login");

        form.current.validateAll();
        if (checkBtn.current.context._errors.length === 0) {
            try {
                const response = await dispatch(login(formData.email, formData.password));
                if (response?.challengeId) {
                    updateChallengeState(response);
                    showPopup(t("auth.login.challenge.feedback.codeSent"));
                    return;
                }

                resetChallengeStep();
                navigate(postLoginRedirect, { replace: true });
            } catch (error) {
                if (isAccountDeletionRecoveryRequired(error)) {
                    const recoveryData = error?.response?.data || {};
                    const recoveryPath = recoveryData.recoveryPath === ACCOUNT_DELETION_ROUTES.recovery
                        ? recoveryData.recoveryPath
                        : ACCOUNT_DELETION_ROUTES.recovery;
                    clearLoginFeedback();
                    setAccountDeletionRecoveryPrompt({
                        email: formData.email.trim(),
                        recoveryPath,
                        recoveryAvailable: recoveryData.recoveryAvailable !== false,
                        recoveryUnavailableReason: recoveryData.recoveryUnavailableReason || null,
                        deletionStatus: recoveryData.deletionStatus || null,
                        recoverableUntil: recoveryData.recoverableUntil || null,
                    });
                    setFormData((current) => ({
                        ...current,
                        password: "",
                    }));
                    return;
                }
                setPopupTrigger((prev) => prev + 1);
            } finally {
                setLoadingAction(null);
            }
        } else {
            setLoadingAction(null);
        }
    };

    const handleChallengeResend = async () => {
        if (!loginChallenge || isChallengeExpired || resendCooldownRemaining > 0) {
            return;
        }

        setLoadingAction("resend");
        try {
            const response = await dispatch(resendLoginChallenge(loginChallenge.challengeId));
            updateChallengeState(response);
            showPopup(t("auth.login.challenge.feedback.codeResent"));
        } catch (error) {
            if (error?.response?.status === 404 || error?.response?.status === 409) {
                clearPendingLoginChallenge();
                setLoginChallenge((current) => (
                    current
                        ? {
                            ...current,
                            expiresAt: Date.now(),
                        }
                        : current
                ));
            }
            if (error?.response?.status === 429) {
                showPopup(t("auth.login.challenge.feedback.rateLimited"));
                return;
            }
            showPopup(getChallengeErrorMessage(error));
        } finally {
            setLoadingAction(null);
        }
    };

    const handleChallengeSubmit = async (event) => {
        event.preventDefault();
        if (!loginChallenge) {
            return;
        }

        if (isChallengeExpired) {
            showPopup(t("auth.login.challenge.feedback.challengeExpired"));
            return;
        }

        const validationMessage = getChallengeValidationMessage();
        if (validationMessage) {
            showPopup(validationMessage);
            return;
        }

        setLoadingAction("challenge");
        try {
            await dispatch(completeLoginChallenge(loginChallenge.challengeId, verificationCode));
            resetChallengeStep();
            navigate(postLoginRedirect, { replace: true });
        } catch (error) {
            if (error?.response?.status === 404 || error?.response?.status === 409) {
                clearPendingLoginChallenge();
                setLoginChallenge((current) => (
                    current
                        ? {
                            ...current,
                            expiresAt: Date.now(),
                        }
                        : current
                ));
            }
            showPopup(getChallengeErrorMessage(error));
        } finally {
            setLoadingAction(null);
        }
    };

    const handleSsoStart = async (provider) => {
        if (loginChallenge) {
            return;
        }

        setSsoLoading(provider);
        try {
            const authorizeUrl = await startSso(provider, postLoginRedirect);
            if (authorizeUrl) {
                window.location.assign(authorizeUrl);
                return;
            }
            showPopup(t("auth.login.sso.error"));
        } catch (error) {
            showPopup(t("auth.login.sso.error"));
        } finally {
            setSsoLoading(null);
        }
    };

    const dismissAccountDeletionRecoveryPrompt = () => {
        clearLoginFeedback();
        setAccountDeletionRecoveryPrompt(null);
    };

    const openAccountDeletionRecovery = () => {
        if (!accountDeletionRecoveryPrompt) {
            return;
        }
        navigate(accountDeletionRecoveryPrompt.recoveryPath, {
            state: {
                recoveryEmail: accountDeletionRecoveryPrompt.email,
                recoveryAvailable: accountDeletionRecoveryPrompt.recoveryAvailable,
                recoveryUnavailableReason: accountDeletionRecoveryPrompt.recoveryUnavailableReason,
                deletionStatus: accountDeletionRecoveryPrompt.deletionStatus,
                recoverableUntil: accountDeletionRecoveryPrompt.recoverableUntil,
            },
        });
    };

    const resendLabel = resendCooldownRemaining > 0
        ? t("auth.login.challenge.actions.resendCountdown", {
            time: formatDuration(resendCooldownRemaining),
        })
        : t("auth.login.challenge.actions.resend");

    return (
        <>
            {/* The recovery panel already states the situation in full; the
                left-over login error would otherwise float above it saying the
                same thing twice. */}
            {message && !accountDeletionRecoveryPrompt && (
                <PopupMessage
                    message={message}
                    popupTrigger={popupTrigger}
                    contentClassName="popup-content--auth-default"
                />
            )}

            <LiquidGlassCard
                title={<AuthBrandTitle label={t("auth.common.brand")} />}
                variant="auth"
                size="medium"
                className="login-card"
            >
                {loginChallenge ? (
                    <>
                        <p className="auth-support-copy">
                            {t("auth.login.challenge.descriptions.start")}
                        </p>
                        <p className="auth-form-note">
                            {t("auth.login.challenge.descriptions.note")}
                        </p>
                        <p className="auth-form-note auth-form-note--reminder">
                            {t("auth.common.emailDeliveryHint")}
                        </p>
                        <div
                            className={`auth-status ${isChallengeExpired ? "auth-status--error" : ""}`}
                            role="status"
                            aria-live="polite"
                        >
                            {isChallengeExpired
                                ? t("auth.login.challenge.status.expired")
                                : t("auth.login.challenge.status.expiresIn", {
                                    time: formatDuration(verificationWindowRemaining),
                                })}
                        </div>

                        <form onSubmit={handleChallengeSubmit} className="login-form" noValidate>
                            <div className="form-group">
                                <input
                                    ref={verificationInputRef}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    className="form-control"
                                    name="verificationCode"
                                    placeholder={t("auth.login.challenge.fields.code.placeholder")}
                                    aria-label={t("auth.login.challenge.fields.code.placeholder")}
                                    value={verificationCode}
                                    onChange={handleVerificationCodeChange}
                                    disabled={isCompletingChallenge}
                                    maxLength={VERIFICATION_CODE_LENGTH}
                                />
                            </div>

                            <div className="form-group">
                                <button className="btn login-btn" disabled={isCompletingChallenge || isChallengeExpired}>
                                    {isCompletingChallenge && <span className="spinner-border spinner-border-sm" />}
                                    <span>{t("auth.login.challenge.actions.verify")}</span>
                                </button>
                            </div>
                        </form>

                        <div className="auth-secondary-actions">
                            <button
                                type="button"
                                className="auth-secondary-button"
                                onClick={handleChallengeResend}
                                disabled={isCompletingChallenge || isResendingChallenge || isChallengeExpired || resendCooldownRemaining > 0}
                            >
                                {resendLabel}
                            </button>
                            <button
                                type="button"
                                className="auth-secondary-button"
                                onClick={resetChallengeStep}
                                disabled={isCompletingChallenge || isResendingChallenge}
                            >
                                {t("auth.login.challenge.actions.restart")}
                            </button>
                        </div>
                    </>
                ) : accountDeletionRecoveryPrompt ? (
                    /* Recovery replaces the sign-in form rather than sitting on top
                       of it. The credentials that produced this state cannot sign
                       anyone in, so leaving the form, the SSO button and the
                       register link on screen put three dead ends around the only
                       two actions that actually work. */
                    <div className="login-recovery" role="status" aria-live="polite">
                        <h2 className="login-recovery__title">
                            {t(accountDeletionRecoveryPrompt.recoveryAvailable
                                ? "auth.login.accountDeletionRecovery.title"
                                : "auth.login.accountDeletionRecovery.unavailableTitle")}
                        </h2>
                        <p className="login-recovery__description">
                            {t(accountDeletionRecoveryPrompt.recoveryAvailable
                                ? "auth.login.accountDeletionRecovery.description"
                                : "auth.login.accountDeletionRecovery.unavailableDescription")}
                        </p>
                        <div className="login-recovery__actions">
                            <button
                                type="button"
                                className="login-recovery__action login-recovery__action--primary"
                                onClick={openAccountDeletionRecovery}
                            >
                                {t(accountDeletionRecoveryPrompt.recoveryAvailable
                                    ? "auth.login.accountDeletionRecovery.actions.restore"
                                    : "auth.login.accountDeletionRecovery.actions.openRecovery")}
                            </button>
                            <button
                                type="button"
                                className="login-recovery__action login-recovery__action--secondary"
                                onClick={dismissAccountDeletionRecoveryPrompt}
                            >
                                {t("auth.login.accountDeletionRecovery.actions.back")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* `noValidate` matches every other auth form. Without it the
                            browser ran its own constraint check on the `type="email"`
                            field first, showing an untranslated native bubble that
                            dismissed itself moments later and swallowed the submit,
                            so the app's own inline message never got to render. */}
                        <Form onSubmit={handleSubmit} ref={form} className="login-form" noValidate>
                            <div className="form-group">
                                <Input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    placeholder={t("auth.login.fields.email.placeholder")}
                                    aria-label={t("auth.login.fields.email.placeholder")}
                                    value={formData.email}
                                    onChange={onChange}
                                    validations={[requiredValidator, emailFormatValidator]}
                                />
                            </div>

                            <div className="form-group">
                                <Input
                                    type="password"
                                    className="form-control"
                                    name="password"
                                    placeholder={t("auth.login.fields.password.placeholder")}
                                    aria-label={t("auth.login.fields.password.placeholder")}
                                    value={formData.password}
                                    onChange={onChange}
                                    validations={[requiredValidator]}
                                />
                            </div>

                            <div className="form-group">
                                <button className="btn login-btn" disabled={isLoggingIn}>
                                    {isLoggingIn && <span className="spinner-border spinner-border-sm" />}
                                    <span>{t("auth.login.actions.submit")}</span>
                                </button>
                            </div>

                            <CheckButton style={{ display: "none" }} ref={checkBtn} />
                        </Form>

                        <p className="auth-footer auth-footer--secondary">
                            <Link to="/forgot-password">{t("auth.login.footer.forgotLink")}</Link>
                        </p>

                        <div className="sso-divider">
                            <span>{t("auth.login.sso.divider")}</span>
                        </div>

                        <div className="sso-buttons">
                            <ContinueWithGoogleButton
                                onClick={() => handleSsoStart("google")}
                                disabled={isLoggingIn || ssoLoading !== null}
                                isLoading={ssoLoading === "google"}
                                label={t("auth.login.sso.google")}
                                loadingLabel={t("auth.login.sso.loading")}
                            />
                            {/* Milestone 2: re-enable Apple SSO after provider configuration is complete.
                            <ContinueWithAppleButton
                                onClick={() => handleSsoStart("apple")}
                                disabled={isLoggingIn || ssoLoading !== null}
                                isLoading={ssoLoading === "apple"}
                                label={t("auth.login.sso.apple")}
                                loadingLabel={t("auth.login.sso.loading")}
                            />
                            */}
                        </div>

                        <p className="auth-footer">
                            {t("auth.login.footer.prompt")}{" "}
                            <Link to="/register">{t("auth.login.footer.link")}</Link>
                        </p>
                    </>
                )}
            </LiquidGlassCard>
        </>
    );
};

export default LoginCard;
